// GLTFWorkerPool
//
// Pool of Web Workers that parse GLB ArrayBuffers off the main thread.
// Each parse round-trips bytes (or transferable buffers — see below)
// to a worker; the worker calls GLTFLoader.parse and returns a
// serialized geometry+material bag. The main thread reconstructs real
// BufferGeometry and Material objects from that bag — cheap, just
// wraps the TypedArrays the worker already produced.
//
// === WHY ===
//
// In the rolled-back single-threaded pipeline, GLTFLoader.parse runs on
// the main thread for every fragment GLB. With 6,000+ unique hashes
// in a real BIM model, parse work pegs the main thread for tens of
// seconds and competes with render — the user reported "the last 6%
// only completes when I look away."
//
// Moving parse to workers frees the main thread. Render keeps 60fps
// while many parses run in parallel across worker cores.
//
// === POOL SIZING ===
//
// Start fast, grow on demand. A few workers are spawned in the
// constructor so parsing can begin within milliseconds of the first
// fragment arriving, and one more is added each time work has to wait
// on a fully busy pool, up to a ceiling of 2x the core count (max 24).
//
// Parsing is CPU work: more workers than cores parse nothing faster,
// they only cost memory and delay the first parse while the same
// worker script is loaded over and over. A small model never pays for
// workers it will not use; a cold load of a big model reaches the
// ceiling inside the first second. Both ends are tunable via the
// constructor (`size` = ceiling, `initial` = spawned up front).
//
// === FALLBACK ===
//
// If `new Worker` throws (Workers disabled / older CefSharp / blob
// CSP), `available=false` and `parseGLB` rejects. Callers should fall
// back to main-thread parse (see GLBFetcher).
//
// === BUFFER OWNERSHIP ===
//
// We DON'T transfer the input buffer to the worker. The buffer is
// structured-cloned (a copy is made). Why: if the worker fails or
// is unavailable, the caller's fallback path (main-thread parse) needs
// the original buffer. The structured-clone cost is small (~1ms for
// typical 50–200 KB GLBs) vs. main-thread parse cost (~5–20ms), so
// the trade is well worth it.
//
// We DO transfer the output buffers from worker → main (the geometry
// TypedArrays). Those have no caller-side fallback need and represent
// the actual size of the geometry data, so zero-copy matters.

import {
  BufferGeometry, BufferAttribute,
  MeshStandardMaterial, MeshBasicMaterial, MeshPhongMaterial, MeshLambertMaterial,
  Color, FrontSide,
} from 'three';

const _materialCtors = {
  MeshStandardMaterial,
  MeshBasicMaterial,
  MeshPhongMaterial,
  MeshLambertMaterial,
};

const DEFAULT_WORKER_URL = '/gltfParseWorker.js';

export default class GLTFWorkerPool {
  constructor({ workerUrl = DEFAULT_WORKER_URL, size = null, initial = null } = {}) {
    this.workerUrl = workerUrl;
    // See POOL SIZING above. `maxSize` is the ceiling the pool grows
    // to; `initialSize` is what the constructor spawns up front.
    const cores = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4;
    this.maxSize = Math.max(1, size ?? Math.max(4, Math.min(24, cores * 2)));
    this.initialSize = Math.max(1, Math.min(this.maxSize, initial ?? Math.min(8, this.maxSize)));

    this.workers = [];       // [{ worker, busy }]
    this.queue = [];         // [{ id, buffer, opts }]
    this.pending = new Map(); // id -> { resolve, reject }
    this.nextId = 1;

    // Counters for the diagnostic snippet.
    this._totalParses = 0;
    this._totalParseErrors = 0;

    this.available = false;
    try {
      for (let i = 0; i < this.initialSize; i++) this._spawn();
      this.available = this.workers.length > 0;
    } catch (err) {
      console.warn(
        '[GLTFWorkerPool] worker construction failed, pool disabled. ' +
        'Main thread parse will be used as fallback. error:', err);
      this.available = false;
      this.workers = [];
    }
  }

  // Number of tasks waiting for a free worker. Exposed so GLBFetcher
  // can route to main-thread parse when this exceeds the worker count
  // (work-stealing): no point queueing into workers if main thread has
  // a free slot.
  get queueLength() { return this.queue.length; }

  // Workers alive right now. Grows from initialSize towards maxSize.
  get size() { return this.workers.length; }

  _spawn() {
    const w = new Worker(this.workerUrl);
    const slot = { worker: w, busy: false, index: this.workers.length };
    w.onmessage = (e) => this._onReply(slot, e.data);
    w.onerror = (e) => this._onWorkerError(slot, e);
    this.workers.push(slot);
    return slot;
  }

  // Add one worker when a parse finds every worker busy and the
  // ceiling has not been reached. Growth follows real demand, so it
  // only happens while the viewer is actually under load.
  _growIfSaturated() {
    if (!this.available) return null;
    if (this.workers.length >= this.maxSize) return null;
    if (this.workers.some(w => !w.busy)) return null;
    try { return this._spawn(); } catch (e) { return null; }
  }

  // True iff at least one worker is currently idle.
  hasFreeWorker() {
    if (!this.available) return false;
    // A pool that can still grow counts as having a free worker: the
    // next parseGLB spawns one rather than queueing. Without this the
    // fetcher would steal work back to the main thread while the pool
    // is still below its ceiling.
    return this.workers.some(w => !w.busy) || this.workers.length < this.maxSize;
  }

  // Parse a GLB ArrayBuffer. Returns Promise<{ libraryParts }> where
  // libraryParts matches the existing GLBFetcher shape (3-element array
  // of { geometry, material, name }, with missing LODs filled with the
  // first available).
  //
  // The input buffer is structured-cloned (NOT transferred) so the
  // caller can still use it for a fallback parse if the worker fails.
  parseGLB(arrayBuffer) {
    if (!this.available) {
      return Promise.reject(new Error('GLTFWorkerPool unavailable'));
    }
    return new Promise((resolve, reject) => {
      const id = this.nextId++;
      this.pending.set(id, { resolve, reject });
      const free = this.workers.find(w => !w.busy) || this._growIfSaturated();
      if (free) {
        this._dispatch(free, id, arrayBuffer);
      } else {
        this.queue.push({ id, buffer: arrayBuffer });
      }
    });
  }

  _dispatch(slot, id, buffer) {
    slot.busy = true;
    this._totalParses++;
    try {
      // No transferable list — structured-clone the buffer. Reasoning
      // in the file-level comment.
      slot.worker.postMessage({ id, buffer });
    } catch (err) {
      slot.busy = false;
      const p = this.pending.get(id);
      if (p) {
        this.pending.delete(id);
        p.reject(err);
      }
    }
  }

  _onReply(slot, msg) {
    slot.busy = false;
    const p = this.pending.get(msg.id);
    if (!p) {
      // Stale reply from a worker we already forgot about. Just drain.
    } else {
      this.pending.delete(msg.id);
      if (msg.ok) {
        try {
          const libraryParts = this._reconstructLibraryParts(msg.meshes);
          // Scene extras ride along on the array so a caller that wants
          // them (the merged singleton meshes, whose uid table lives
          // there) can read them without a second protocol.
          if (msg.extras) libraryParts.extras = msg.extras;
          p.resolve({ libraryParts });
        } catch (err) {
          this._totalParseErrors++;
          p.reject(err);
        }
      } else {
        this._totalParseErrors++;
        p.reject(new Error(msg.error || 'GLTFWorkerPool: parse failed'));
      }
    }
    // Drain queue.
    if (this.queue.length > 0) {
      const next = this.queue.shift();
      this._dispatch(slot, next.id, next.buffer);
    }
  }

  _onWorkerError(slot, evt) {
    console.warn(
      `[GLTFWorkerPool] worker ${slot.index} fired error:`,
      evt && evt.message ? evt.message : evt);
    // We don't tear down — the worker might recover from a single
    // bad message. If it's truly dead, subsequent postMessage calls
    // will fail and bubble up to the caller as a rejection.
  }

  _reconstructLibraryParts(meshes) {
    // Convert worker's serialized meshes back into real three.js
    // objects. Each mesh entry → { geometry, material, name }.
    const parts = meshes.map((m) => ({
      geometry: this._reconstructGeometry(m),
      material: this._reconstructMaterial(m.material),
      name: m.name,
    }));
    // Match GLBFetcher's existing "fill empty slots from first non-null"
    // semantic. Library parts is always length 3.
    while (parts.length < 3) {
      parts.push(parts.find(Boolean) ?? null);
    }
    return parts;
  }

  _reconstructGeometry(m) {
    const g = new BufferGeometry();
    for (const key of Object.keys(m.attributes)) {
      const a = m.attributes[key];
      g.setAttribute(key, new BufferAttribute(a.array, a.itemSize, a.normalized));
    }
    if (m.index) {
      g.setIndex(new BufferAttribute(m.index.array, 1));
    }
    return g;
  }

  _reconstructMaterial(props) {
    if (!props) return new MeshStandardMaterial();
    const Ctor = _materialCtors[props.type] || MeshStandardMaterial;
    const init = {
      color: new Color(props.color != null ? props.color : 0xffffff),
      opacity: props.opacity != null ? props.opacity : 1,
      transparent: !!props.transparent,
      side: props.side != null ? props.side : FrontSide,
      flatShading: !!props.flatShading,
      // Fragments written by FragmentGlbWriter put colour on the vertices
      // (COLOR_0) so a LOD mesh needs one material and emits exactly one
      // primitive. GLTFLoader sets vertexColors on the material it built
      // inside the worker; without carrying it across, this rebuilt
      // material ignores COLOR_0 and every fragment renders flat white.
      vertexColors: !!props.vertexColors,
      name: props.name || '',
    };
    const mat = new Ctor(init);
    // Blend state travels with the material: a BLEND fragment arrives with
    // transparent=true and depthWrite=false from GLTFLoader, and the pool
    // must keep both or the per-vertex alpha in COLOR_0 is ignored.
    if (props.depthWrite != null) mat.depthWrite = !!props.depthWrite;
    if (props.depthTest != null) mat.depthTest = !!props.depthTest;
    if (props.alphaTest) mat.alphaTest = props.alphaTest;
    // Properties not common to all material types — set defensively.
    if (props.roughness != null && 'roughness' in mat) mat.roughness = props.roughness;
    if (props.metalness != null && 'metalness' in mat) mat.metalness = props.metalness;
    if (props.emissive != null && mat.emissive) mat.emissive.setHex(props.emissive);
    return mat;
  }

  stats() {
    return {
      available: this.available,
      size: this.workers.length,
      maxSize: this.maxSize,
      busy: this.workers.filter(w => w.busy).length,
      queued: this.queue.length,
      pending: this.pending.size,
      totalParses: this._totalParses,
      totalParseErrors: this._totalParseErrors,
    };
  }

  dispose() {
    for (const slot of this.workers) {
      try { slot.worker.terminate(); } catch (e) { /* swallow */ }
    }
    this.workers = [];
    this.queue = [];
    // Reject pending promises so callers don't hang.
    for (const [, p] of this.pending) {
      try { p.reject(new Error('GLTFWorkerPool disposed')); } catch (e) {}
    }
    this.pending.clear();
    this.available = false;
  }
}
